import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase"; // Firebase auth and db instances
import {
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true); // Initial loading state

  async function logout() {
    return signOut(auth);
  }

  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    try {
      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupError) {
        // Safari veya popup engellemelerinde redirect'e düş
        if (
          popupError?.code === "auth/popup-blocked" ||
          popupError?.code ===
            "auth/operation-not-supported-in-this-environment"
        ) {
          await signInWithRedirect(auth, provider);
          // Redirect dönüşünde sonucu almak için
          result = await getRedirectResult(auth);
          if (!result) throw popupError;
        } else {
          throw popupError;
        }
      }
      const user = result.user;
      // Kullanıcı Firestore'da var mı diye kontrol et
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        // Yeni kullanıcı ise Firestore'a kaydet
        console.log(
          "AuthContext: New user signed in with Google, creating Firestore document."
        );
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || "Google User",
          photoURL: user.photoURL,
          role: "user", // Varsayılan rol
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        });
      } else {
        // Mevcut kullanıcı ise son giriş zamanını güncelle (opsiyonel)
        console.log(
          "AuthContext: Existing user signed in with Google, updating lastLoginAt."
        );
        await setDoc(
          userDocRef,
          { lastLoginAt: serverTimestamp() },
          { merge: true }
        );
      }
      // onAuthStateChanged geri kalanını halledecek (currentUser'ı ayarlama vb.)
      return user;
    } catch (error) {
      console.error("AuthContext: Google Sign-In Error", error);
      // Hata yönetimi (örn: kullanıcıya bildirim gösterme)
      throw error;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("AuthContext: onAuthStateChanged triggered", user?.uid);
      if (user) {
        // Kullanıcı giriş yapmış, admin kontrolü yap
        const decodedToken = await user.getIdTokenResult();
        console.log("AuthContext: Admin claims check", decodedToken.claims.admin);
        
        if (decodedToken.claims.admin) {
          // Firestore'dan kullanıcı bilgilerini al (photoURL için)
          let userPhotoURL = user.photoURL;
          let userDisplayName = user.displayName;
          let userData = null;
          
          try {
            console.log("AuthContext: Fetching user data for UID:", user.uid);
            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (userDocSnap.exists()) {
              userData = userDocSnap.data();
              console.log("AuthContext: User document found:", userData);
              // Firestore'daki photoURL varsa onu kullan (Firebase Auth'ta yoksa)
              if (userData.photoURL) {
                userPhotoURL = userData.photoURL;
                console.log("AuthContext: Using photoURL from Firestore:", userData.photoURL);
              }
              // DisplayName de Firestore'dan al
              if (userData.displayName && !userDisplayName) {
                userDisplayName = userData.displayName;
              }
            } else {
              console.log("AuthContext: No user document found in Firestore for UID:", user.uid);
            }
          } catch (error) {
            console.warn("Could not fetch user data from Firestore:", error);
          }
          
          console.log("Firebase Auth photoURL:", user.photoURL);
          console.log("Firestore userData:", userData);
          console.log("Final photoURL being set:", userPhotoURL);
          
          // Force refresh Firestore data every time
          const freshUserRef = doc(db, "users", user.uid);
          const freshUserSnap = await getDoc(freshUserRef);
          if (freshUserSnap.exists()) {
            const freshData = freshUserSnap.data();
            console.log("Fresh Firestore data:", freshData);
            if (freshData.photoURL) {
              userPhotoURL = freshData.photoURL;
              console.log("Using fresh photoURL:", freshData.photoURL);
            }
          }
          
          setCurrentUser({
            uid: user.uid,
            email: user.email,
            displayName: userDisplayName,
            photoURL: userPhotoURL,
            isAdmin: true,
          });
        } else {
          await signOut(auth);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // İleride login, signup gibi fonksiyonları buraya ekleyebiliriz.
  const value = {
    currentUser,
    loading,
    logout,
    signInWithGoogle, // signInWithGoogle fonksiyonunu context değerine ekle
    // login: async (email, password) => { /* ... */ },
    // signup: async (email, password) => { /* ... */ },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}{" "}
      {/* Yükleme durumunu route bileşenleri yönetecek, çocukları her zaman render et */}
    </AuthContext.Provider>
  );
}
